import { LinkIcon, LocationIcon } from '@/assets/icons'
import ConfirmationModal, { DeletionTarget, Disclaimer } from '@components/ConfirmationModal'
import { useDroppable } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { EventCategory } from '@orbit/api-types'
import dayjs from 'dayjs'
import React, { useRef, useState } from 'react'
import EditEventModal from '../EditEventModal'
import {
    CardContainer,
    CardDescription,
    CardHeader,
    CardLinks,
    CardLocation,
    CardMeta,
    CardTitle,
    LinkInfo,
    LinkPreviewCard,
    LinksHeader,
    LinkSource,
    LinkThumbnail,
    LinkUrl,
    LocationHeader,
    LocationMapLink,
    LocationText,
    TimeSlot,
} from './Card.styles'
import { CardProps } from './types'

const Card: React.FC<CardProps> = ({ event, onUpdate, onDelete }) => {
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const cardRef = useRef<HTMLDivElement>(null)
	const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null)

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: `event-${event.id}`,
	})

	const { setNodeRef: setDroppableRef, isOver } = useDroppable({
		id: `card-${event.id}`,
	})

	// Hide the card when dragging within the same column
	if (isDragging) {
		return (
			<div
				ref={setNodeRef}
				style={{
					transform: CSS.Transform.toString(transform),
					transition,
					opacity: 0,
					height: 0,
					margin: 0,
					padding: 0,
					overflow: 'hidden',
				}}
				{...attributes}
				{...listeners}
			/>
		)
	}

	const handleMouseDown = (e: React.MouseEvent) => {
		// Track mouse down position to detect if user is clicking or dragging
		mouseDownPosRef.current = { x: e.clientX, y: e.clientY }
	}

	const handleCardClick = (e: React.MouseEvent) => {
		// Only open modal if this was a simple click (no significant movement)
		// The drag activation requires 8px movement, so if mouse moved less than 5px, treat as click
		if (mouseDownPosRef.current) {
			const deltaX = Math.abs(e.clientX - mouseDownPosRef.current.x)
			const deltaY = Math.abs(e.clientY - mouseDownPosRef.current.y)
			const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

			if (distance < 5) {
				// This was a click, not a drag
				setIsEditModalOpen(true)
			}
			mouseDownPosRef.current = null
		}
	}

	const handleEditSubmit = (
		title: string,
		description: string,
		category?: EventCategory,
		location?: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		},
		links?: string[],
		startTime?: string | null,
	) => {
		onUpdate(event.id.toString(), {
			name: title,
			description: description,
			...(category && { category }),
			...(location !== undefined && { location: location || null }),
			...(links !== undefined && { tripEventLinks: links }),
			...(startTime !== undefined && { startTime }),
		})
	}

	const handleEditCancel = () => {
		setIsEditModalOpen(false)
	}

	const handleDeleteClick = () => {
		setIsDeleteModalOpen(true)
	}

	const handleDeleteConfirm = () => {
		onDelete(event.id.toString())
		setIsDeleteModalOpen(false)
		setIsEditModalOpen(false)
	}

	const handleDeleteCancel = () => {
		setIsDeleteModalOpen(false)
	}

	const style = {}

	// Combine refs for both sortable and droppable
	const combinedRef = (node: HTMLDivElement | null) => {
		setNodeRef(node)
		setDroppableRef(node)
		if (cardRef.current !== node) {
			;(cardRef as React.MutableRefObject<HTMLDivElement | null>).current = node
		}
	}

	// Format time from "HH:mm" (24-hour) to "h:mm a" (12-hour with AM/PM)
	const formatTime = (timeString: string): string => {
		if (!timeString) return ''
		try {
			// Parse the time string (e.g., "15:30") and format it as "3:30 PM"
			const time = dayjs(`2000-01-01T${timeString}`)
			return time.format('h:mm A')
		} catch {
			return timeString // Fallback to original if parsing fails
		}
	}

	return (
		<>
			<CardContainer
				ref={combinedRef}
				style={style}
				$isDragging={isDragging}
				$isOver={isOver}
				{...attributes}
				{...listeners}
				onMouseDown={handleMouseDown}
				onClick={handleCardClick}
			>
				<CardHeader>
					<CardTitle>{event.name}</CardTitle>
				</CardHeader>

				{event.startTime && (
					<CardMeta>
						<TimeSlot>{formatTime(event.startTime)}</TimeSlot>
					</CardMeta>
				)}

				{event.description && <CardDescription>{event.description}</CardDescription>}

				{event.location?.primaryText && (
					<CardLocation>
						<LocationHeader>
							<LocationIcon />
							Location
						</LocationHeader>
						<LocationText>{event.location.primaryText}</LocationText>
						<LocationMapLink
							href={
								event.location.googlePlaceId
									? `https://www.google.com/maps/place/?q=place_id:${event.location.googlePlaceId}`
									: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location.primaryText)}`
							}
							target="_blank"
							rel="noopener noreferrer"
							onClick={e => e.stopPropagation()}
						>
							See On Google Maps →
						</LocationMapLink>
					</CardLocation>
				)}

				{event.tripEventLinks && event.tripEventLinks.length > 0 && (
					<CardLinks onClick={e => e.stopPropagation()}>
						<LinksHeader>
							<LinkIcon />
							Links
						</LinksHeader>
						{event.tripEventLinks.map(link => {
							const hasProtocol = link.url.startsWith('http://') || link.url.startsWith('https://')
							const looksLikeUrl = link.url.includes('.') && !link.url.includes(' ')

							let hrefUrl = link.url
							if (!hasProtocol && looksLikeUrl) {
								hrefUrl = `https://${link.url}`
							}

							const isClickable = hasProtocol || looksLikeUrl

							const getSourceColor = (source: string) => {
								switch (source.toLowerCase()) {
									case 'tiktok':
										return '#000000'
									case 'instagram':
										return '#E4405F'
									case 'youtube':
										return '#FF0000'
									case 'googlemaps':
										return '#4285F4'
									case 'website':
										return '#6b7280'
									default:
										return '#6b7280'
								}
							}

							const formatSourceName = (source: string) => {
								if (source === 'GoogleMaps') return 'Google Maps'
								return source
							}

							return (
								<LinkPreviewCard
									key={link.id}
									href={isClickable ? hrefUrl : undefined}
									target={isClickable ? '_blank' : undefined}
									rel={isClickable ? 'noopener noreferrer' : undefined}
									as={isClickable ? 'a' : 'div'}
									style={!isClickable ? { cursor: 'default' } : {}}
								>
									<LinkThumbnail src={link.thumbnail || undefined}>
										{!link.thumbnail && (
											<LinkIcon width={20} height={20} fill="#9ca3af" />
										)}
									</LinkThumbnail>
									<LinkInfo>
										<LinkSource style={{ color: getSourceColor(link.source) }}>
											{formatSourceName(link.source)}
										</LinkSource>
										<LinkUrl>{link.url}</LinkUrl>
									</LinkInfo>
								</LinkPreviewCard>
							)
						})}
					</CardLinks>
				)}
			</CardContainer>

			<EditEventModal
				isOpen={isEditModalOpen}
				onClose={handleEditCancel}
				onSubmit={handleEditSubmit}
				onDelete={handleDeleteClick}
				event={event}
			/>

			<ConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={handleDeleteCancel}
				onConfirm={handleDeleteConfirm}
				title="Delete Event"
				message={
					<>
						Are you sure you want to delete '<DeletionTarget>{event.name}</DeletionTarget>'?
						<Disclaimer>This action cannot be undone</Disclaimer>
					</>
				}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>
		</>
	)
}

export default Card
